<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { orderBy, debounce } from "lodash-es";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { formGet, formPatch, formPut } from "@/common/api/rest/form/form";
import {
  questionAdd,
  questionEdit,
  questionDel,
} from "@/common/api/rest/question";

import type { MethodItemType } from "@/common/types/method";
import type { FormType, FormPayloadType } from "@/common/types/form";
import type {
  QuestionType,
  QuestionPayloadType,
} from "@/common/types/question";

import { RegisterTheme } from "@/common/themes";
import { formsForm, questionForm, searchMethodItem } from "@/components";
import {
  svgIcon,
  baseTable,
  baseModal,
  baseConfirm,
  baseTooltip,
} from "@/common/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const tableHeader = computed(() => [
  { key: "ordering", label: t("common.labels.order") },
  { key: "name", label: t("common.labels.name") },
  { key: "type", label: t("common.labels.type") },
  { key: "required", label: t("common.labels.required") },
]);

const tableData = ref<QuestionType[]>([]);

const methodItem = ref<MethodItemType>();

const formPayload = ref<FormPayloadType | null>(null);
const questionPayload = ref<QuestionPayloadType | null>(null);
const selected = ref<QuestionType | null>(null);
const seletedMethod = ref<MethodItemType | null>(null);

const isLoading = ref(false);
const isEdit = ref(false);
const showQuestionAdd = ref(false);
const showQuestionDel = ref(false);
const showMethodAdd = ref(false);
const showMethodDel = ref(false);

const accountId = computed(() => String(route.params.id));
const formId = computed(() => String(route.params.form));

const delMessage = computed(() => {
  const message = t("question.texts.del_confirm", {
    concat: selected.value?.name,
  });
  const undo = t("common.texts.undo_message");

  return `${message}<br>${undo}`;
});

function back() {
  router.push(`/${accountId.value}/register/form`);
}

function fetchForm() {
  isLoading.value = true;

  formGet(formId.value)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        console.log("data", data);
        formPayload.value = {
          name: data.name,
          code: data.code,
          description: data.description,
        };
        methodItem.value = data.method_item;
        tableData.value = orderBy(data.questions, "ordering");
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function patchData(data: Partial<FormType>) {
  formPatch(formId.value, data)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchForm();
        toast({ message: data.message, type: "success" });
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateForm(data: FormPayloadType) {
  formPut(formId.value, data)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchForm();
        toast({ message: data.message, type: "success" });
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addQuestion() {
  showQuestionAdd.value = true;
}

function addQuestionCancel() {
  isEdit.value = false;
  selected.value = null;
  questionPayload.value = null;
  showQuestionAdd.value = false;
}

function addQuestionConfirm() {
  if (!questionPayload.value) return;

  store.commit("SET_LOADING", true);

  questionAdd(formId.value as string, questionPayload.value)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchForm();
        addQuestionCancel();
        toast({ message: data.message, type: "success" });
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editQuestion(data: QuestionType) {
  questionPayload.value = data;
  selected.value = data;
  isEdit.value = true;
  showQuestionAdd.value = true;
}

function editQuestionConfirm() {
  if (!questionPayload.value) return;

  store.commit("SET_LOADING", true);

  questionEdit(selected.value?.question_id as string, questionPayload.value)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchForm();
        addQuestionCancel();
        toast({ message: data.message, type: "success" });
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function delQuestion(data: QuestionType) {
  selected.value = data;
  showQuestionDel.value = true;
}

function delQuestionCancel() {
  selected.value = null;
  showQuestionDel.value = false;
}

function delQuestionConfirm() {
  store.commit("SET_LOADING", true);

  questionDel(selected.value?.question_id as string)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchForm();
        delQuestionCancel();
        toast({ message: data.message, type: "success" });
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function addMethod() {
  showMethodAdd.value = true;
}

function addMethodCancel() {
  showMethodAdd.value = false;
  seletedMethod.value = null;
}

function addMethodSelect(data: MethodItemType) {
  seletedMethod.value = data;
}

function addMethodConfirm() {
  patchData({
    method_id: seletedMethod.value?.method_id,
    method_item_id: seletedMethod.value?.item_id,
  });
  addMethodCancel();
}

function delMethod() {
  showMethodDel.value = true;
}

function delMethodCancel() {
  showMethodDel.value = false;
}

function delMethodConfirm() {
  patchData({
    method_id: null,
    method_item_id: null,
  });

  showMethodDel.value = false;
}

function questionSubmit() {
  if (isEdit.value) editQuestionConfirm();
  else addQuestionConfirm();
}

const debounceForm = debounce(updateForm, 1000);

onMounted(() => {
  if (formId.value) {
    fetchForm();
  }
});
</script>

<template>
  <register-theme tab-active="form">
    <template #title>
      <div class="btn btn-back btn-sm" @click="back()">
        <svg-icon icon="arrows-left" size="1.25rem" />
      </div>
      {{ t("form.texts.edit_form") }}
    </template>

    <template #content>
      <forms-form
        width="100%"
        :data="formPayload"
        @update-data="debounceForm"
      />

      <div class="title">
        {{ t("common.labels.method") }}
      </div>
      <div class="methods">
        <template v-if="!methodItem">
          <div class="method-empty">
            {{ t("form.texts.empty_method") }}
            <button class="btn btn-md btn-primary" @click="addMethod()">
              {{ t("common.labels.add").toUpperCase() }}
            </button>
          </div>
        </template>
        <template v-else>
          <div class="method">
            <div class="remove">
              <base-tooltip position="relative">
                <template #trigger>
                  <button
                    type="button"
                    class="btn btn-none btn-sm remove"
                    @click="delMethod()"
                  >
                    <svg-icon icon="trash" size="1.75rem" />
                  </button>
                </template>
                <template #tooltip>
                  {{ t("common.labels.delete") }}
                </template>
              </base-tooltip>
            </div>
            <div class="code">
              {{ methodItem.code }}
            </div>
            <div class="name">
              {{ methodItem.name }}
            </div>
          </div>
        </template>
      </div>

      <div class="title">
        {{ t("common.labels.questions") }}
      </div>

      <baseTable
        :header="tableHeader"
        :data="tableData"
        :loading="isLoading"
        :options="true"
        :edit="true"
        :del="true"
        :paginated="false"
        @btn:add="addQuestion()"
        @btn:edit="editQuestion"
        @btn:del="delQuestion"
      >
        <template #type="{ data }">
          {{ t(`question.types.${data.type}`) }}
        </template>

        <template #required="{ data }">
          <template v-if="data.required">
            {{ t("common.labels.yes") }}
          </template>
          <template v-else>
            {{ t("common.labels.not") }}
          </template>
        </template>
      </baseTable>
    </template>
  </register-theme>

  <base-modal
    width="80%"
    :show="showQuestionAdd"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.confirm')"
    :button-close="t('common.labels.cancel')"
    @close="addQuestionCancel()"
    @confirm="questionSubmit()"
  >
    <template #content>
      <question-form
        width="100%"
        :order="tableData.length + 1"
        :title="
          isEdit
            ? t('question.texts.edit_question')
            : t('question.texts.add_question')
        "
        :data="questionPayload"
        @update-data="questionPayload = $event"
      />
    </template>
  </base-modal>

  <base-modal
    :show="showMethodAdd"
    :title="
      t('common.labels.add', {
        concat: t('common.labels.method'),
      }).toUpperCase()
    "
    :show-button-confirm="true"
    :button-confirm="t('common.labels.add')"
    :button-close="t('common.labels.cancel')"
    @close="addMethodCancel()"
    @confirm="addMethodConfirm()"
  >
    <template #content>
      <search-method-item
        width="600px"
        :account-id="accountId"
        @update-data="addMethodSelect"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showQuestionDel"
    :title="t('question.texts.del_question').toUpperCase()"
    :message="delMessage"
    max-width="500px"
    @cancel="delQuestionCancel()"
    @confirm="delQuestionConfirm()"
  />

  <base-confirm
    :show="showMethodDel"
    :title="
      t('common.labels.delete', {
        concat: t('common.labels.method'),
      }).toUpperCase()
    "
    :message="
      t('form.texts.del_method', {
        concat: methodItem?.code,
      })
    "
    max-width="500px"
    @cancel="delMethodCancel()"
    @confirm="delMethodConfirm()"
  />
</template>

<style lang="scss" scoped>
.title {
  display: flex;
  flex-direction: column;
  padding-inline: 0.5rem;
  border-bottom: 1px solid var(--border);
  margin-block: 2rem;
  padding-block-end: 0.5rem;
  font-size: 1.15rem;
  font-weight: bold;
  text-transform: uppercase;
}

.methods {
  .method {
    padding-inline-start: 1rem;
    .remove {
      display: flex;
      flex-direction: column;
      align-items: flex-end;

      button {
        &:hover {
          color: var(--red);
        }
      }
    }

    .code {
      font-size: 1.15rem;
      font-weight: bold;
      margin-block-end: 0.5rem;
    }

    .name {
      font-size: 1rem;
      margin-block-end: 1rem;
    }
  }

  .method-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block: 1.5rem;
  }
}
</style>